import { FC, useCallback, useMemo, useEffect, memo } from "react";
//import { type ComponentProcessProps } from "components/system/Apps/RenderComponent";
import RndWindow from "./RndWindow";
import StyledWindow from "./StyledWindow";
import Titlebar from "./Titlebar";
import useFocusable from "./useFocusable";
import useWindowTransitions from "./useWindowTransitions";
import { useWindows } from "@/contexts/windows";
import { useSession } from "@/contexts/session";
import { useMainElements } from "@/contexts/elements";
import { WindowProvider } from "@/contexts/windows/window";
import styled from "styled-components";
import ScrollBars from "@/styles/common/ScrollBars";
import { defaultStyles } from "@/styles/defaults";
import { useSignal } from "@/contexts/signal";
import { useConfig } from "@/contexts/config";
import { useWindow } from "@/contexts/windows/window";

const StyledPeekViewport = styled.div`
  ${defaultStyles}
  background-color: inherit;
  height: inherit;
  width: inherit;
`;

const StyledWindowWrapper = styled.div`
  ${defaultStyles}
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  justify-content: center;
`;

const StyledWindowContent = styled.div`
  ${ScrollBars({ scrollbarGutter: false })}
  flex: 1;
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  overflow: auto;
`;
// TODO: Take care of the different styled components inside the acutal components,
// TODO: So <Taskbar> Should take care of which styled component to actually render within.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Window: FC<any> = ({ children, id }) => {
  const {
    linkElement,
    windows: { [id]: currentWindow },
  } = useWindows();
  const {
    backgroundColor,
    Component,
    hideTitlebar,
    peekElement,
    maximized,
    snap,
  } = currentWindow || {};
  const { disableAnimations } = useConfig();
  const { foregroundId } = useSession();
  const isForeground = id === foregroundId;
  const { zIndex, ...focusableProps } = useFocusable(id);
  const { previewID } = useSession()
  const windowTransitions = useWindowTransitions(id, maximized);
  const linkViewportEntry = useCallback(
    (viewportEntry: HTMLDivElement) => {
      if (Component && !peekElement && viewportEntry) {
        linkElement(id, "peekElement", viewportEntry);
      }
    },
    [Component, id, linkElement, peekElement]
  );
  const isSnap = snap && typeof snap === "object";
  const isPreview = previewID !== false;
  const isPreviewAndNotOnUs = isPreview && previewID !== id

  //const isPreview = previewID !== false;
  //const highlightPreview = previewID !== false && previewID == id;
  return (
    <WindowProvider rd_window_id={id} id={id}>
      <RndWindow id={id} zIndex={zIndex} data-rd-type="rd_rnd_window">
        <StyledWindow
          as={disableAnimations && 'section'}
          $rounded={!maximized && !isSnap}
          $showShadows={!disableAnimations && isForeground && !maximized && !isSnap}
          $backgroundColor={backgroundColor}
          $isForeground={isForeground}
          //$previewOnMe={highlightPreview}
          //$isPreview={isPreview}
          {...disableAnimations && isPreview && ({
            style: {
              opacity: isPreviewAndNotOnUs ? "0.12" : "1"
            }
          })}
          {...focusableProps}
          {...(!disableAnimations && windowTransitions || {})}
          data-rd-type="rd_window"
        >
          <StyledPeekViewport
            data-rd-type="rd_link_viewport_entry"
            ref={linkViewportEntry}
          >
            <StyledWindowWrapper data-rd-type="rd_window_wrapper">
              {!hideTitlebar && <Titlebar id={id} />}
              <StyledWindowContent data-rd-type="rd_window_content">
                {children}
              </StyledWindowContent>
            </StyledWindowWrapper>
          </StyledPeekViewport>
        </StyledWindow>
      </RndWindow>
    </WindowProvider>
  );
};
const MemoWindow = memo(Window);

const WindowWithContext: FC<unknown> = ({ children, id, ...others }) => {
  return (
    <WindowProvider rd_window_id={id} id={id}>
      <MemoWindow id={id} {...others}>
        {children}
      </MemoWindow>
    </WindowProvider>
  );
};

export default memo(WindowWithContext);
